import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import FAQAccordion from "@/components/FAQAccordion";
import RiskDisclosure from "@/components/RiskDisclosure";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { COURSES, FAQS } from "@/data/content";

const CourseDetail = () => {
  const { slug } = useParams();
  const course = COURSES.find((c) => c.slug === slug);

  useEffect(() => {
    if (course) document.title = `${course.title} — ${course.category} Course | T Plus One`;
  }, [course]);

  if (!course) return <Navigate to="/courses" replace />;

  const relatedFaqs = FAQS.filter((f) => f.category === "Courses" || f.category === "EnTrade").slice(0, 6);

  return (
    <main className="pt-32 lg:pt-40" data-testid={`course-page-${course.slug}`}>
      {/* HERO */}
      <header className={`container-x pb-20 lg:pb-28 ${course.exclusive ? "" : ""}`}>
        <Reveal><p className="eyebrow">{course.index} / {course.category}</p></Reveal>
        <MaskReveal as="h1" lines={[course.title + "."]} className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight" />
        <Reveal delay={0.2}>
          <p className="mt-6 text-xl sm:text-2xl text-dim tracking-tight max-w-2xl">{course.tagline}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to={`/contact?interest=${encodeURIComponent(`${course.title} — ${course.category}`)}`}
              data-testid="course-enquire-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink text-ivory px-8 py-4 text-sm font-medium transition-colors hover:bg-pine"
            >
              Enquire About {course.title} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
            <a href="#curriculum" data-testid="course-curriculum-link" className="link-arrow inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-sm font-medium transition-colors hover:border-pine hover:text-pine">
              View Curriculum <ArrowRight size={15} className="arrow" />
            </a>
          </div>
        </Reveal>
      </header>

      {/* OVERVIEW + STICKY INFO */}
      <section className="border-t border-line bg-white">
        <div className="container-x py-20 lg:py-28 grid lg:grid-cols-[1fr_360px] gap-14">
          <div>
            <SectionHeader eyebrow="Overview" lines={["What this program covers."]} />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base sm:text-lg text-dim leading-relaxed">{course.overview}</p>
            </Reveal>

            <Reveal delay={0.1} className="mt-16">
              <h2 className="text-2xl sm:text-3xl tracking-tight">Who this is for</h2>
            </Reveal>
            <ul className="mt-8 space-y-4">
              {course.whoFor.map((w) => (
                <Reveal key={w}>
                  <li className="flex gap-4 items-start">
                    <Check size={17} className="mt-1 text-pine shrink-0" aria-hidden="true" />
                    <span className="text-base text-dim leading-relaxed">{w}</span>
                  </li>
                </Reveal>
              ))}
            </ul>

            <Reveal className="mt-16">
              <h2 className="text-2xl sm:text-3xl tracking-tight">What you’ll learn</h2>
            </Reveal>
            <div className="mt-8 flex flex-wrap gap-2">
              {course.topics.map((t) => (
                <span key={t} className="text-sm rounded-full px-4 py-2 border border-line text-dim bg-ivory">{t}</span>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start" data-testid="course-info-panel">
            <div className={`rounded-sm border p-8 ${course.exclusive ? "bg-coal text-ivory border-coal" : "bg-ivory border-line"}`}>
              <dl className="space-y-5">
                {[["Duration", course.duration], ["Mode", course.mode], ["Level", course.level], ["Course Fee", course.fee], ["Community", "Lifetime access"]].map(([k, v]) => (
                  <div key={k} className={`flex justify-between gap-4 pb-5 border-b last:border-0 last:pb-0 ${course.exclusive ? "border-coalline" : "border-line"}`}>
                    <dt className={`text-sm ${course.exclusive ? "text-[#A0A09C]" : "text-dim"}`}>{k}</dt>
                    <dd className="text-sm font-medium text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={`/contact?interest=${encodeURIComponent(`${course.title} — ${course.category}`)}`}
                data-testid="course-info-enquire-btn"
                className="mt-8 block text-center rounded-full bg-pine text-ivory py-3.5 text-sm font-medium transition-colors hover:bg-pine-2"
              >
                Enquire Now
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 lg:py-32 border-t border-line scroll-mt-20">
        <div className="container-x">
          <SectionHeader eyebrow="Curriculum" lines={["A deliberate sequence."]} sub="Each module builds on the last — fundamentals before complexity, always." />
          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {course.curriculum.map((mod, i) => (
              <Reveal key={mod.title} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-sm border border-line bg-white p-8" data-testid={`curriculum-module-${i}`}>
                  <p className="eyebrow">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 text-xl tracking-tight">{mod.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {mod.items.map((it) => (
                      <li key={it} className="text-sm text-dim flex gap-3"><span className="text-pine-2">—</span>{it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORMAT + COMMUNITY */}
      <section className="relative bg-coal text-ivory py-24 lg:py-32 grain overflow-hidden">
        <div className="container-x grid lg:grid-cols-2 gap-14">
          <div>
            <Reveal><p className="eyebrow-dark">Learning Format</p></Reveal>
            <MaskReveal as="h2" lines={["Online or offline.", "Same curriculum."]} className="mt-6 text-3xl sm:text-4xl tracking-tight leading-[1.12]" />
            <Reveal delay={0.2}>
              <p className="mt-6 text-base text-[#A0A09C] leading-relaxed max-w-md">
                {course.title} runs for {course.duration.toLowerCase()} and is available both online and in the classroom. Choose the format that fits how you learn best.
              </p>
            </Reveal>
          </div>
          <div>
            <Reveal><p className="eyebrow-dark">Premium Community</p></Reveal>
            <MaskReveal as="h2" lines={["Lifetime access,", "included."]} className="mt-6 text-3xl sm:text-4xl tracking-tight leading-[1.12]" />
            <Reveal delay={0.2}>
              <p className="mt-6 text-base text-[#A0A09C] leading-relaxed max-w-md">
                Every {course.title} student receives lifetime access to the T Plus One Premium Community — market insights, educational trade setups and ongoing learning after the program ends.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-t border-line">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <SectionHeader eyebrow="FAQ" lines={["Course questions."]} />
          <FAQAccordion items={relatedFaqs} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 bg-white border-t border-line text-center">
        <div className="container-x max-w-2xl mx-auto">
          <Reveal><p className="eyebrow">{course.title}</p></Reveal>
          <MaskReveal as="h2" lines={["Ready to begin?"]} className="mt-6 text-4xl sm:text-5xl tracking-tight" />
          <Reveal delay={0.2}>
            <p className="mt-6 text-dim">Send an enquiry and the T Plus One team will walk you through batches, formats and enrolment.</p>
            <Link
              to={`/contact?interest=${encodeURIComponent(`${course.title} — ${course.category}`)}`}
              data-testid="course-final-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-ink text-ivory px-8 py-4 text-sm font-medium transition-colors hover:bg-pine"
            >
              Enquire About {course.title} <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={0.3} className="mt-14 text-left">
            <RiskDisclosure />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CourseDetail;
